<script lang="ts">
	//Svelte hooks
	import { onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { shendure } from 'src/services/http';
	//Stores
	import { home_data } from 'src/stores/account/user';
import { devices } from 'src/stores/lists/devices';
import { device_object } from 'src/stores/wizards';
	import { devices_config } from '@devices/index';
	//Design system components
	import {
		CardGrid,
		Text,
		Button,
		Loader,
		AdvancedCard,
		disableWrapperFocus,
	} from '@Shelly/shelly-design-system';

	import EmptyList from 'src/components/Lists/EmptyList.svelte';
	import type { Device } from 'src/types/Device';
	import { addDeviceModal } from 'src/stores/device-runtime/firmware-ui';
	import { capFirstChar } from 'src/helpers/utilities';
	import { ZENDURE_MODELS } from 'src/constants';

	$: availableDevices =
		typeof allZendureDevices !== 'undefined'
			? Object.values(allZendureDevices).filter((d: any) => {
					const model = transformZendureModel(d.model);
					d.type = ZENDURE_MODELS.includes(model) ? model : 'XPZ-SOLARFLOW';
					d.displayID = d.id;
					d.selected = $devices[d.id] !== undefined;
					return d && $devices[d.id] === undefined;
				})
			: [];

	$: includedDevices =
		typeof allZendureDevices !== 'undefined'
			? Object.values(allZendureDevices).filter((d: any) => {
					if ($devices[d.id] !== undefined) {
						const model = transformZendureModel(d.model);
						d.type = ZENDURE_MODELS.includes(model) ? model : 'XPZ-SOLARFLOW';
						d.displayID = d.id;
						d.selected = $devices[d.id] !== undefined;

						return d;
					}
				})
			: [];

	$: availableDevicesCount = Object.values(availableDevices).length;
	$: includedDevicesCount = Object.values(includedDevices).length;

	let allZendureDevices = {};
	let loading = false;

	onMount(async () => {
		if ($home_data?.zendure) {
			try {
				loading = true;
				let res = await shendure.get('list_devices');

				if (res?.isok) {
					allZendureDevices = res?.data || {};
				}
			} catch (error) {
				console.error('cannot get zendure devices');
			} finally {
				loading = false;
			}
		}
	});

	const transformZendureModel = (model: string) => {
		// Return the model in the format XPZ-<MODEL>
		return `XPZ-${model?.toUpperCase()?.replace(/\s+/g, '')}`;
	};

	const handleDeviceAssociate = (device) => {
		let selectedDevice: Device = {
			id: device.id,
			type: device.type,
			category: devices_config[device.type]?.family,
			position: -1,
			gen: 'GXP',
			channel: 0,
			channels_count: 1,
			mode: '',
			name: device?.name || '',
			room_id: undefined,
			no_room_cons: true,
			no_account_cons: true,
			image: `images/device_images/${device.type}.png`,
			cloud_options: { exclude_event_log: false },
			bundle: false,
			ip: device?.ip,
		};

		device_object.reset();
		device_object.set({
			...selectedDevice,
			enableCloudAfterAccountAssociation: false,
		});
		addDeviceModal.set(true);
	};
</script>

{#if loading}
	<div class="d-flex justify-content-center align-items-center">
		<Loader size="m" />
	</div>
{:else if !availableDevicesCount && !includedDevicesCount}
	<EmptyList
		title={$format('empty_zendure_list_title', {
			default: 'No Zendure devices available',
		})}
		description={$format('empty_zendure_list_description', {
			default: 'There are no devices associated with this Zendure account',
		})}
		withoutMargin={true}
	/>
{:else}
	<div class="d-flex justify-content-center align-items-center mb-l">
		<Text element="h1" class="text-center">
			{$format('text_choose_device_inclusion', {
				default: 'Choose the device you want to include',
			})}
		</Text>
	</div>

	{#if includedDevicesCount}
		<Text element="h2" class="mb-s">
			{$format('text_already_added', {
				default: `Already added (${includedDevicesCount})`,
				values: { count: includedDevicesCount },
			})}
		</Text>
		<CardGrid sm={200} md={300} lg={300} noPaddingBottom>
			{#each includedDevices as d}
				<div use:disableWrapperFocus>
					<AdvancedCard
						isCondensed
						class="width-full"
						type="horizontal"
						avatar={{
							image: `images/device_images/${d.type}.png`,
						}}
						title={`${d.name}`}
						description={{
							value: `${d.id}`,
							type: 'muted',
							bold: false,
							textSmall: true,
						}}
					/>
				</div>
			{/each}
		</CardGrid>

		{#if availableDevicesCount}
			<hr class="mb-m mt-m" />
		{/if}
	{/if}

	{#if availableDevicesCount}
		<Text element="h2" class="mb-s">
			{$format('text_available_devices_count', {
				default: `Available devices (${availableDevicesCount})`,
				values: { count: availableDevicesCount },
			})}
		</Text>
		<CardGrid sm={200} md={300} lg={300} noPaddingBottom>
			{#each availableDevices as discoveredDevice}
				<div use:disableWrapperFocus>
					<AdvancedCard
						isCondensed
						class="width-full"
						type="horizontal"
						avatar={{
							image: `images/device_images/${discoveredDevice.type}.png`,
						}}
						title={`${discoveredDevice.name}`}
						description={{
							value: `${discoveredDevice.id}`,
							type: 'muted',
							bold: false,
							textSmall: true,
						}}
					>
						<div slot="right" class="d-flex align-items-center">
							<Button
								size="m"
								on:click={() => {
									handleDeviceAssociate(discoveredDevice);
								}}>{capFirstChar($format('button_add', { default: 'Add' }))}</Button
							>
						</div>
					</AdvancedCard>
				</div>
			{/each}
		</CardGrid>
	{/if}
{/if}
